var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import localforage from 'localforage';
import { accountScope } from './account-scope';
/*
 * VA-29255: unsaved form work kept on this device (IndexedDB via localforage), so a
 * dropped connection, a failed save or a reload doesn't cost the recruiter what they
 * typed.
 *
 * - Keys are scoped to the signed-in organization AND user, so one account never
 *   sees another's drafts on a shared machine; logout clears the whole store.
 * - Every record carries a schema version and expires after DRAFT_TTL_MS; anything
 *   stale or from an older shape is deleted on read, never offered.
 * - Never put tokens, passwords, files or candidate data in here.
 *
 * A separate store from src/utils/storage.ts, which belongs to the candidate interview.
 */
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const store = localforage.createInstance({ name: 'vriti-drafts', storeName: 'recruiter_drafts' });
/** Storage key for one form and one record (e.g. 'job-wizard', a JD id or 'new'). */
export function draftKey(form, record) {
    const scope = accountScope();
    return scope ? `${scope}:${form}:${record}` : null;
}
export function readDraft(key, version) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const record = yield store.getItem(key);
            if (!record)
                return null;
            if (record.v !== version || Date.now() - record.savedAt > DRAFT_TTL_MS) {
                yield store.removeItem(key);
                return null;
            }
            return record;
        }
        catch (_a) {
            return null;
        }
    });
}
/** Resolves false when the browser refused the write (private mode, quota, blocked storage). */
export function writeDraft(key, version, data) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            yield store.setItem(key, { v: version, savedAt: Date.now(), data });
            return true;
        }
        catch (_a) {
            return false;
        }
    });
}
export function deleteDraft(key) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            yield store.removeItem(key);
        }
        catch (_a) {
            // Nothing to do: an undeletable draft still expires.
        }
    });
}
/** Deletes expired drafts, so ones that are never opened again don't linger past the TTL. */
export function pruneExpiredDrafts() {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const expired = [];
            yield store.iterate((record, key) => {
                if (!record || Date.now() - record.savedAt > DRAFT_TTL_MS)
                    expired.push(key);
            });
            yield Promise.all(expired.map((key) => store.removeItem(key)));
        }
        catch (_a) {
            // Best effort.
        }
    });
}
/** Logout: drop every draft on this device, whoever wrote it. */
export function clearAllDrafts() {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            yield store.clear();
        }
        catch (_a) {
            // Storage unavailable: there was nothing persisted to clear.
        }
    });
}
